import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Typography,
  Container,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const categoriesData = [
  {
    name: 'Massage',
    options: [
      'Couple',
      'Deep Tissue',
      'Hot Stone',
      'Lomi Lomi',
      'Lymphatic',
      'Prenatal',
      'Reflexology',
      'Sports',
      'Swedish',
      'Thai',
    ],
  },
  {
    name: 'Beauty',
    options: [
      'Aesthetic',
      'Facials',
      'Hair Color',
      'Haircut',
      'Hair Removal',
      'Lashes/Brows',
      'Manicures',
      'Pedicures',
      'Piercing',
      'Skin Care',
    ],
  },
  {
    name: 'Fitness',
    options: [
      'Bodybuilding',
      'Cardio',
      'Cycling',
      'Personal Training',
      'Pilates',
      'Running',
      'Self Defense',
      'Weightlifting',
      'Yoga',
      'Zomba',
    ],
  },
  {
    name: 'Nutrition',
    options: [
      'Diabetic',    
      'Eating Disorder',
      'Food Allergies',
      'Heart-Health',
      'Kidney-Health',
      'Oncology',
      'Pregnancy',
      'Protein Diet',
      'Stomach Discomfort',
      'Weight Loss',
    ],
  },
  {
    name: 'Tutoring',
    options: [
      'Dance',
      'Digital Art',
      'Music',
      'Painting',
      'Pottery',
      'Biology',
      'Chemistry',
      'Computer',
      'Mathematics',
      'Physics',
    ],
  },
  {
    name: 'Wealth',
    options: [
      'Credit Management',
      'Estate Planning',
      'Financial Planning',
      'Investment Advice',
      'Legal Advice',
      'LLC Formation',
      'Personal Accounting',
      'Portfolio Management',
      'Retirement Planning',
      'Tax Preparation',
    ],
  },
];

function PlannerStep2() {
  const location = useLocation();
  const resultRef = useRef(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selfCareNeeds, setSelfCareNeeds] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zipError, setZipError] = useState('');
  const [resultString, setResultString] = useState('');
  const theme = useTheme();
  const [demographics, setDemographics] = useState({
    gender: '',
    age: '',
    income: '',
    preferredTime: '',
    preferredDays: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const interestsParam = params.get('interests') || '';
    const interests = interestsParam.split(';').filter((item) => item);
    const formattedInterests = interests.map(
      (cat) => cat.charAt(0).toUpperCase() + cat.slice(1)
    );
    setSelectedCategories(formattedInterests);
    setDemographics({
      gender: params.get('gender') || '',
      age: params.get('age') || '',
      income: params.get('income') || '',
      preferredTime: params.get('preferredTime') || '',
      preferredDays: params.get('preferredDays') || '',
    });
  }, [location.search]);

  useEffect(()=> {
    console.log(resultString)
  }, [resultString])

  const handleSelfCareNeedsChange = (e) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/);
    if (words.filter((word) => word !== '').length <= 100) {
      setSelfCareNeeds(text);
    }
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value;
    setZipCode(value);
    if (!/^\d{5}$/.test(value)) {
      setZipError('Zip code must be 5 digits');
    } else {
      setZipError('');
    }
  };

  const handleSubmit = () => {
    if (!/^\d{5}$/.test(zipCode)) {
      setZipError('Zip code must be 5 digits');
      return;
    }
    let result = '';
    selectedCategories.forEach((category) => {
      result += category + '\n';
      const options = selectedOptions[category] || [];
      options.forEach((option) => {
        result += '   ' + option + '\n';
      });
    });
    result += '\nDemographic Information:\n';
    result += `Gender: ${demographics.gender}\n`;
    result += `Age: ${demographics.age}\n`;
    result += `Income: ${demographics.income}\n`;
    result += `Preferred Time: ${demographics.preferredTime}\n`;
    result += `Preferred Days: ${demographics.preferredDays}\n`;
    result += `Zip Code: ${zipCode}\n\n`;
    result += selfCareNeeds;
    setResultString(result);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const [currentStep, setCurrentStep] = useState(0);

  const handleToggleOption = (categoryName, option) => {
    setSelectedOptions((prev) => {
      const currentSelections = prev[categoryName] || [];
      const alreadySelected = currentSelections.includes(option);
      const updatedSelections = alreadySelected
        ? currentSelections.filter((o) => o !== option)
        : [...currentSelections, option];
      return {
        ...prev,
        [categoryName]: updatedSelections,
      };
    });
  };

  const handleNextMobile = () => {
    if (currentStep < selectedCategories.length) {
      setCurrentStep(currentStep + 1);
    } else {
      console.log('Final submission', selectedOptions);
      handleSubmit();
    }
  };

  const progressValue = ((currentStep + 1) / (selectedCategories.length + 1)) * 100;

  return (
    <>
      <Container
        sx={{
          height: 'calc(100vh - 180px)',
          overflowY: 'auto',
          width: '100%',
          marginBottom: '50px',
          marginRight: 'auto',
          marginLeft: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mt: 4

        }}
      >
        <Typography variant="h4" component="h1">
          Selfcare Planner
        </Typography>
        <Divider sx={{ width: '100%', my: 2, backgroundColor: 'gray' }} />
        <>
          {selectedCategories.map((category, index) => {
            const categoryData = categoriesData.find((cat) => cat.name === category);
            if (!categoryData) return null;
            return (
              <Accordion
                key={categoryData.name}
                expanded={currentStep === index}
                onChange={(event, isExpanded) => {
                  if (isExpanded) setCurrentStep(index);
                }}
                sx={{ width: '100%',
                  boxShadow: 'none',
                  borderBottom: '1px solid lightgray',
                  "&::before": {
                    display: "none",
                  },
                  "&.Mui-expanded": {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{categoryData.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {categoryData.options.map((option) => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={selectedOptions[categoryData.name]?.includes(option) || false}
                            onChange={() => handleToggleOption(categoryData.name, option)}
                          />
                        }
                        label={option}
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            );
          })}
          <Accordion
            key="zip"
            expanded={currentStep === selectedCategories.length}
            onChange={(event, isExpanded) => {
              if (isExpanded) setCurrentStep(selectedCategories.length);
            }}
            sx={{width: '100%',
              boxShadow: 'none',
              borderBottom: '1px solid lightgray',
              "&::before": {
                display: "none",
              },
              "&.Mui-expanded": {
                margin: 0,
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Additional Info</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mt: 3, width: '100%' }}>
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  fullWidth
                  value={zipCode}
                  onChange={handleZipCodeChange}
                  error={!!zipError}
                  helperText={zipError || 'Enter a valid 5-digit zip code'}
                />
              </Box>
              <Box sx={{ mt: 3, width: '100%' }}>
                <TextField
                  label="Tell us about your selfcare needs in a bullet list"
                  multiline
                  fullWidth
                  minRows={5}
                  variant="outlined"
                  value={selfCareNeeds}
                  onChange={handleSelfCareNeedsChange}
                  helperText="Max 100 words"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      </Container>
      <Box sx={{ height: '100px' }} />
      <Box
        sx={{
          position: 'fixed',
          height: '90px',
          bottom: 'var(--footer-height, 50px)',
          left: 0,
          right: 0,
          backgroundColor: theme.palette.background.paper,
          padding: 0,
          textAlign: 'center',
          zIndex: 1000,
          maxWidth: 'calc(100vw)',
        }}
      >
        <Box id={"progressBox"} sx={{ marginBottom: 1, marginTop: 1 }}>
          <LinearProgress variant="determinate" value={progressValue} />
          <Typography variant="caption">
            {currentStep + 1} of {selectedCategories.length + 1}
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleNextMobile} sx={{ marginBottom: '10px' }}>
          {currentStep === selectedCategories.length ? 'Submit' : 'Next'}
        </Button>
      </Box>
    </>
  );
}

export default PlannerStep2;